import React, { useEffect, useRef } from "react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const MySwal = withReactContent(Swal);

const SweetAlert = ({
  title,
  children, // This is often used for description in react-bootstrap-sweetalert
  success,
  danger,
  error,
  warning,
  info,
  custom,
  customIcon,
  showCancel,
  showConfirm = true,
  showCloseButton = false,
  confirmBtnText,
  cancelBtnText,
  confirmBtnBsStyle,
  cancelBtnBsStyle,
  onConfirm,
  onCancel,
  timeout,
  disabled,
}) => {
  // Use a ref to track if this instance is unmounted
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    let icon = "info";
    if (success) icon = "success";
    if (danger || error) icon = "error";
    if (warning) icon = "warning";
    if (custom) icon = undefined;

    // Bootstrap colors mapped for standard appearance
    const colorMap = {
      success: "#28a745",
      danger: "#dc3545",
      primary: "#007bff",
      warning: "#ffc107",
      info: "#17a2b8",
      dark: "#343a40",
      secondary: "#6c757d",
    };

    // Construct content (children usually contain the description)
    const contentHtml = children ? (
      <div style={{ marginTop: "15px" }}>{children}</div>
    ) : undefined;

    const swalConfig = {
      title: title || "",
      icon: customIcon ? undefined : icon,
      imageUrl: customIcon || undefined,
      html: contentHtml,
      showCancelButton: showCancel || false,
      showConfirmButton: showConfirm,
      showCloseButton: showCloseButton,
      confirmButtonText: confirmBtnText || "OK",
      cancelButtonText: cancelBtnText || "Cancel",
      confirmButtonColor: colorMap[confirmBtnBsStyle] || "#007bff",
      cancelButtonColor: colorMap[cancelBtnBsStyle] || "#6c757d",
      timer: timeout || undefined,
      timerProgressBar: !!timeout,
      allowOutsideClick: false,
      allowEscapeKey: false,
      reverseButtons: true,
      didOpen: () => {
        if (disabled) {
          MySwal.showLoading();
        }
      },
      preConfirm: () => {
        const confirmBtn = MySwal.getConfirmButton();
        if (confirmBtn) {
          const originalText = confirmBtn.textContent || "";
          confirmBtn.textContent = originalText.toLowerCase().includes("delete")
            ? "Deleting..."
            : "Processing...";
        }

        MySwal.disableButtons(); // Officially disables the buttons with correct CSS styling

        if (confirmBtn) {
          confirmBtn.style.cursor = "not-allowed";
        }
        const cancelBtn = MySwal.getCancelButton();
        if (cancelBtn) {
          cancelBtn.style.cursor = "not-allowed";
        }

        if (onConfirm) {
          onConfirm();
        }

        // Return false to prevent SweetAlert2 from auto-closing
        // The modal will now only close when the parent unmounts it (e.g. when API succeeds)
        return false;
      },
    };

    MySwal.fire(swalConfig).then((result) => {
      // We still handle cancel and timer dismissals
      if (result.isDismissed) {
        if (onCancel) onCancel();
        else if (result.dismiss === Swal.DismissReason.timer && onConfirm) {
          onConfirm();
        }
      }
    });

    return () => {
      isMounted.current = false;
      // Close this specific swal if it's still open when unmounted
      if (MySwal.isVisible()) {
        MySwal.close();
      }
    };
  }, [
    title,
    children,
    success,
    danger,
    error,
    warning,
    info,
    custom,
    customIcon,
    showCancel,
    showConfirm,
    showCloseButton,
    confirmBtnText,
    cancelBtnText,
    confirmBtnBsStyle,
    cancelBtnBsStyle,
    onConfirm,
    onCancel,
    timeout,
    disabled,
  ]);

  return null; // This component doesn't render any DOM itself
};

export default SweetAlert;
